import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import classes from "../../CampaignsDashboard.module.scss";
import { CampaignType } from "../../../../../types/campaignTypes";
import { transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { truncateText } from "../../../../../helpers/truncateText";

type CampaignReportDashboardProps = {
  campaign: CampaignType;
};

const CampaignReportDashboard: FC<CampaignReportDashboardProps> = ({ campaign }) => {
  const columns = [
    { key: "status", label: "Status", minWidth: 100 },
    { key: "name", label: "Name", minWidth: 120 },
    { key: "startDate", label: "Start Date", minWidth: 120 },
    { key: "endDate", label: "End Date", minWidth: 120 },
    { key: "campaignBudget", label: "Campaign Budget", minWidth: 120 },
    { key: "matchedAccounts", label: "Matched Accounts", minWidth: 70 },
    { key: "clicks", label: "Clicks", minWidth: 100 },
    { key: "clickThroughRate", label: "CTR", minWidth: 100 },
    { key: "impressions", label: "Impressions", minWidth: 70 },
    { key: "budgetSpent", label: "Budget Spent", minWidth: 100 },
    { key: "costPerClick", label: "CPC (USD)", minWidth: 100 },
    { key: "costPerMile", label: "CPM (USD)", minWidth: 100 },
    { key: "conversions", label: "Conversions", minWidth: 100 },
    { key: "spentPerConversions", label: "Spent Per Conversions", minWidth: 100 },
    { key: "viewThroughConversions", label: "View Through Conversions", minWidth: 100 },
    { key: "clickThroughConversions", label: "Click Through Conversions", minWidth: 100 },
  ] as const;

  const campaignTimeReport = campaign?.campaignReports?.length ? campaign.campaignReports[0] : null;
  const {
    clicks,
    ctr,
    impressions,
    spend,
    cpm_usd,
    cpc_usd,
    conversions,
    click_through_conversions,
    spend_per_conversion,
    viewthrough_conversions,
  } = campaignTimeReport ?? {};

  const campaignBasicInfo = {
    id: campaign.brandgenCampaignId,
    status: (() =>
      campaign.active ? (
        <Typography className={classes.activeStatus}>ACTIVE</Typography>
      ) : (
        <Typography className={classes.inactiveStatus}>INACTIVE</Typography>
      ))(),
    name: (() => (
      <Tooltip title={campaign.name}>
        <Typography>{truncateText(campaign.name, 20)}</Typography>
      </Tooltip>
    ))(),
    startDate: new Date(campaign.start_date).toLocaleDateString(),
    endDate: new Date(campaign.end_date).toLocaleDateString(),
    campaignBudget: transformRowValue(campaign.campaign_budget, false, true, "CURRENCY"),
    matchedAccounts: +campaign.accounts,
  };

  const campaignAllTimeReportInfo = {
    clicks: transformRowValue(clicks, true, false, "FLAT"),
    clickThroughRate: transformRowValue(ctr, false, true, "PERCENTAGE"),
    impressions: transformRowValue(impressions, true, false, "FLAT"),
    budgetSpent: transformRowValue(spend, false, true, "CURRENCY"),
    costPerMile: transformRowValue(cpm_usd, false, true, "CURRENCY"),
    costPerClick: transformRowValue(cpc_usd, false, true, "CURRENCY"),
    conversions: transformRowValue(conversions, true, false, "FLAT"),
    clickThroughConversions: transformRowValue(click_through_conversions, true, false, "FLAT"),
    viewThroughConversions: transformRowValue(viewthrough_conversions, true, false, "FLAT"),
    spentPerConversions: transformRowValue(spend_per_conversion, false, true, "CURRENCY"),
  };

  const rows = [
    {
      ...campaignBasicInfo,
      ...campaignAllTimeReportInfo,
    },
  ];

  return (
    <div className={classes.reportDashboard}>
      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default CampaignReportDashboard;
